/**
 * Submit for Approval Dialog
 *
 * The documents required at submit-for-approval are configured PER PRODUCT and
 * PER DOCUMENT TYPE. refdata_document_types flagged as submission documents are
 * candidates; each product (Reference Data > Investment Types) chooses which are
 * required, and each document type is either per-contract (one upload) or
 * per-investor (one upload per investor). This dialog renders one section per
 * REQUIRED document type for the contract's product — a product may require
 * none, one, or many. Requirements that don't apply are not shown.
 *
 * Layout is grouped for hierarchy:
 *   - Contract Documents — per-contract types (per_investor=false), signed
 *     contract first (with its "reuse the last rejected submission" affordance),
 *     then the rest by sort_order.
 *   - Investor Documents — per-investor types (per_investor=true) shown as one
 *     SUB-TAB per investor (with a completion indicator: green check when all that
 *     investor's docs are uploaded, else done/total), so many-investor contracts
 *     stay manageable. The selected tab shows that investor's upload rows (each
 *     stored with contract_id AND investor_id).
 *
 * The required set is the contract's snapshot (T3-040). On open, the dialog first
 * re-snapshots the Draft from the product's current requirements (Draft-only), so it
 * always reflects the latest product configuration; the set then freezes at actual
 * submission (Pending Approval onward), keeping approved/active contracts insulated.
 *
 * Flow:
 *   1. On open, GET /contracts/:id/document-requirements (the generic list of
 *      required types, incl. sort_order), GET /contracts/:id/previous-submission-document
 *      (reuse offer for the signed contract), GET /contracts/:id/investors
 *      (per-investor rows), and the contract (single-investor fallback).
 *   2. Upload each required document via POST /admin/documents/upload (per-investor
 *      uploads carry investor_id).
 *   3. POST /contracts/:id/submit-for-approval with { document_ids: [...] } — every
 *      uploaded id. The API/SP enforce the same per-product/per-type policy.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import TabPanel, { a11yProps } from '../common/TabPanel';
import FileDropZone from '../common/FileDropZone';
import { apiGet, apiPost, apiUpload } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { formatDate } from '../../utils/formatters';

const ACCEPTED_MIME = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const ACCEPTED_EXT_RE = /\.(pdf|doc|docx)$/i;
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB — matches API multer limit
const CONTRACT_SIGNED = 'contract_signed';

const validateFile = (f) => {
  if (!ACCEPTED_EXT_RE.test(f.name)) return 'Only PDF, DOC or DOCX files are accepted.';
  if (f.size > MAX_BYTES) return 'File is larger than the 10 MB limit.';
  return null;
};

const SubmitForApprovalDialog = ({ open, contractId, onClose, onSubmitted }) => {
  const [loading, setLoading] = useState(false);
  const [previous, setPrevious] = useState(null);
  const [investors, setInvestors] = useState([]);

  // Required submission document types for this contract's product:
  //   [{ document_type_code, document_type_name, required, per_investor }]
  const [requirements, setRequirements] = useState([]);

  // Signed contract mode: 'reuse' | 'upload' (only relevant to contract_signed).
  const [contractMode, setContractMode] = useState('upload');

  // Uploaded documents, keyed by document_type_code:
  //   per-contract type -> { file, docId, fileName, uploading }
  //   per-investor type -> { [investorId]: { file, docId, fileName, uploading } }
  const [docs, setDocs] = useState({});

  // Selected investor sub-tab (numeric index, clamped to the investor count).
  const [activeInvestorTab, setActiveInvestorTab] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const resetState = useCallback(() => {
    setPrevious(null);
    setInvestors([]);
    setRequirements([]);
    setContractMode('upload');
    setDocs({});
    setActiveInvestorTab(0);
    setError(null);
    setSubmitting(false);
  }, []);

  // Load requirements + previous-submission document + investor list on open.
  // First re-snapshot the contract's required documents from the product's current
  // set (Draft-only; the submit dialog only opens on Drafts) so the dialog always
  // reflects the latest product configuration; then load everything.
  useEffect(() => {
    if (!open || !contractId) return;
    let cancelled = false;
    resetState();
    setLoading(true);

    (async () => {
      // Best-effort refresh of the frozen requirement snapshot from the product.
      // Ignore failures (e.g. the SP raises for a non-Draft) — the GET below still
      // returns the (fallback/live) set.
      try {
        await apiPost(`/contracts/${contractId}/document-requirements/refresh`, {});
      } catch (err) {
        logger.warn('Document-requirement refresh skipped', { message: err?.message });
      }
      if (cancelled) return;

      const [prevRes, invRes, contractRes, reqRes] = await Promise.allSettled([
        apiGet(`/contracts/${contractId}/previous-submission-document`),
        apiGet(`/contracts/${contractId}/investors`),
        apiGet(`/contracts/${contractId}`),
        apiGet(`/contracts/${contractId}/document-requirements`),
      ]);
      if (cancelled) return;

      {
        // Required document types (only the ones marked required for this product).
        if (reqRes.status === 'fulfilled' && Array.isArray(reqRes.value?.data)) {
          setRequirements(reqRes.value.data.filter((r) => r.required));
        } else {
          logger.warn('Failed to load document requirements', { message: reqRes.reason?.message });
          setRequirements([]);
        }

        // Previous submission document (reuse offer for the signed contract).
        if (prevRes.status === 'fulfilled') {
          const data = prevRes.value?.data || null;
          setPrevious(data);
          setContractMode(data ? 'reuse' : 'upload');
        } else {
          logger.warn('Failed to load previous submission document', { message: prevRes.reason?.message });
          setContractMode('upload');
        }

        // Investors (one row each for per-investor types). fn_contract_get_investors
        // only returns contract_investors rows (multi-investor contracts). For a
        // single-investor contract that list is empty, so fall back to the
        // contract's own investor.
        let investorList = [];
        if (invRes.status === 'fulfilled' && Array.isArray(invRes.value?.data) && invRes.value.data.length > 0) {
          investorList = invRes.value.data;
        } else {
          if (invRes.status !== 'fulfilled') {
            logger.warn('Failed to load contract investors', { message: invRes.reason?.message });
          }
          const c = contractRes.status === 'fulfilled' ? (contractRes.value?.data || contractRes.value) : null;
          if (c?.investor_id) {
            investorList = [{ investor_id: c.investor_id, investor_name: c.investor_name }];
          }
        }
        setInvestors(investorList);
      }

      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [open, contractId, resetState]);

  // ---- Generic per-contract document state helpers ----
  const setDoc = (code, patch) => {
    setDocs((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), ...patch } }));
  };
  const setInvestorDoc = (code, investorId, patch) => {
    setDocs((prev) => ({
      ...prev,
      [code]: { ...(prev[code] || {}), [investorId]: { ...((prev[code] || {})[investorId] || {}), ...patch } },
    }));
  };

  // ---- Per-contract upload (one document for the whole contract) ----
  // File-taking core (shared by the FileDropZone choose/drop callback): validate +
  // stage. Passing null clears the slot.
  const acceptFile = (code, f) => {
    setError(null);
    if (!f) { setDoc(code, { file: null, docId: null, fileName: null }); return; }
    const msg = validateFile(f);
    if (msg) { setDoc(code, { file: null, docId: null, fileName: null }); setError(msg); return; }
    setDoc(code, { file: f, docId: null, fileName: null });
  };

  const handleUpload = async (req) => {
    const code = req.document_type_code;
    const entry = docs[code];
    if (!entry?.file) return;
    try {
      setDoc(code, { uploading: true });
      setError(null);
      const formData = new FormData();
      formData.append('file', entry.file);
      formData.append('document_type', code);
      formData.append('contract_id', String(contractId));
      formData.append('notes', `${req.document_type_name} attached on submit-for-approval`);
      const res = await apiUpload('/admin/documents/upload', formData);
      const docId = res?.data?.id;
      if (!docId) throw new Error('Upload succeeded but no document id was returned');
      setDoc(code, { docId, fileName: res?.data?.file_name || entry.file.name, uploading: false });
    } catch (err) {
      setDoc(code, { uploading: false });
      setError(`Failed to upload ${req.document_type_name}: ` + (err?.message || 'Unknown error'));
    }
  };

  // ---- Per-investor upload (one document per investor) ----
  const acceptInvestorFile = (code, investorId, f) => {
    setError(null);
    if (!f) { setInvestorDoc(code, investorId, { file: null, docId: null, fileName: null }); return; }
    const msg = validateFile(f);
    if (msg) { setInvestorDoc(code, investorId, { file: null, docId: null, fileName: null }); setError(msg); return; }
    setInvestorDoc(code, investorId, { file: f, docId: null, fileName: null });
  };

  const handleInvestorUpload = async (req, investor) => {
    const code = req.document_type_code;
    const investorId = investor.investor_id;
    const entry = (docs[code] || {})[investorId];
    if (!entry?.file) return;
    try {
      setInvestorDoc(code, investorId, { uploading: true });
      setError(null);
      const formData = new FormData();
      formData.append('file', entry.file);
      formData.append('document_type', code);
      formData.append('contract_id', String(contractId));
      formData.append('investor_id', String(investorId));
      formData.append('notes', `${req.document_type_name} for ${investor.investor_name || 'investor'} attached on submit-for-approval`);
      const res = await apiUpload('/admin/documents/upload', formData);
      const docId = res?.data?.id;
      if (!docId) throw new Error('Upload succeeded but no document id was returned');
      setInvestorDoc(code, investorId, { docId, fileName: res?.data?.file_name || entry.file.name, uploading: false });
    } catch (err) {
      setInvestorDoc(code, investorId, { uploading: false });
      setError(`Failed to upload ${req.document_type_name} for ${investor.investor_name || 'investor'}: ` + (err?.message || 'Unknown error'));
    }
  };

  // ---- Derived readiness ----
  // A per-contract requirement is met when its doc is uploaded (contract_signed may
  // instead reuse the previous submission's document).
  const isReqMet = (req) => {
    const code = req.document_type_code;
    if (req.per_investor) {
      if (investors.length === 0) return true; // nothing to attach to
      return investors.every((inv) => !!(docs[code] || {})[inv.investor_id]?.docId);
    }
    if (code === CONTRACT_SIGNED && contractMode === 'reuse') return !!previous?.document_id;
    return !!docs[code]?.docId;
  };

  const anyUploading =
    Object.values(docs).some((v) => {
      if (!v) return false;
      if (typeof v.uploading === 'boolean') return v.uploading; // per-contract entry
      return Object.values(v).some((iv) => iv?.uploading);       // per-investor map
    });

  const requirementsMet = requirements.every(isReqMet);
  const canSubmit = !submitting && !anyUploading && requirementsMet;
  const noDocumentsRequired = requirements.length === 0;

  // Grouped, ordered view of the requirements: contract-level documents first
  // (signed contract pinned first, then by sort_order), then per-investor
  // documents (rendered once per investor, all types nested under each).
  const orderReqs = (a, b) => {
    if (a.document_type_code === CONTRACT_SIGNED) return -1;
    if (b.document_type_code === CONTRACT_SIGNED) return 1;
    const sa = a.sort_order ?? 0;
    const sb = b.sort_order ?? 0;
    if (sa !== sb) return sa - sb;
    return a.document_type_code.localeCompare(b.document_type_code);
  };
  const contractDocs = requirements.filter((r) => !r.per_investor).sort(orderReqs);
  const investorDocs = requirements.filter((r) => r.per_investor).sort(orderReqs);

  // Per-investor completion (for the sub-tab indicator): how many of that
  // investor's required per-investor documents are uploaded, and whether all are.
  const investorUploadedCount = (inv) =>
    investorDocs.filter((req) => !!(docs[req.document_type_code] || {})[inv.investor_id]?.docId).length;
  const investorComplete = (inv) => investorDocs.length > 0 && investorUploadedCount(inv) === investorDocs.length;

  // Clamp the selected investor tab to the loaded investor list (it loads async).
  const safeInvestorTab = investors.length > 0 ? Math.min(activeInvestorTab, investors.length - 1) : 0;

  // Collect every uploaded document id (per-contract + per-investor + reused).
  const collectDocumentIds = () => {
    const ids = [];
    for (const req of requirements) {
      const code = req.document_type_code;
      if (req.per_investor) {
        investors.forEach((inv) => {
          const id = (docs[code] || {})[inv.investor_id]?.docId;
          if (id != null) ids.push(id);
        });
      } else if (code === CONTRACT_SIGNED && contractMode === 'reuse') {
        if (previous?.document_id != null) ids.push(previous.document_id);
      } else if (docs[code]?.docId != null) {
        ids.push(docs[code].docId);
      }
    }
    return Array.from(new Set(ids));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      const documentIds = collectDocumentIds();
      const payload = {};
      if (documentIds.length > 0) payload.document_ids = documentIds;
      await apiPost(`/contracts/${contractId}/submit-for-approval`, payload);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      const apiMsg = err?.response?.data?.error?.message || err?.message;
      setError('Failed to submit for approval: ' + (apiMsg || 'Unknown error'));
      setSubmitting(false);
    }
  };


  // ---- Render helpers ----
  const renderReuseCard = () => (
    <Card variant="outlined" sx={{ borderColor: contractMode === 'reuse' ? 'primary.main' : 'divider', borderWidth: contractMode === 'reuse' ? 2 : 1 }}>
      <CardContent>
        <FormControlLabel
          value="reuse"
          control={<Radio />}
          label={
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">Reuse previously uploaded document</Typography>
              <Typography variant="caption" color="text.secondary">The contract document from your last submission attempt</Typography>
            </Box>
          }
        />
        {previous && (
          <Box sx={{ pl: 4, mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <InsertDriveFileIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight="medium">{previous.file_name}</Typography>
            <Chip size="small" label={`Uploaded ${formatDate(previous.uploaded_at)}`} sx={{ ml: 1 }} />
            {previous.rejected_at && <Chip size="small" color="warning" label={`Rejected ${formatDate(previous.rejected_at)}`} />}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Upload card for a per-contract document type. `label` is the "Upload X" heading.
  const renderUploadCard = (req, { asRadioOption } = {}) => {
    const code = req.document_type_code;
    const entry = docs[code] || {};
    const inner = (
      <Box sx={{ pl: asRadioOption ? 4 : 0, mt: asRadioOption ? 2 : 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {entry.docId ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2" fontWeight="medium">{entry.fileName}</Typography>
            <Chip size="small" color="success" label="Uploaded" />
            <Button size="small" onClick={() => setDoc(code, { file: null, docId: null, fileName: null })}>Replace</Button>
          </Box>
        ) : (
          <>
            <FileDropZone
              file={entry.file || null}
              onFile={(f) => acceptFile(code, f)}
              accept={ACCEPTED_MIME}
              disabled={entry.uploading}
              dense
              promptText="Choose a file, or drag and drop"
            />
            <Box>
              <Button variant="contained" size="small" onClick={() => handleUpload(req)} disabled={!entry.file || entry.uploading} startIcon={entry.uploading ? <CircularProgress size={16} color="inherit" /> : null}>
                {entry.uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    );

    if (asRadioOption) {
      // Rendered inside the signed-contract RadioGroup as the "upload new" option.
      return (
        <Card variant="outlined" sx={{ borderColor: contractMode === 'upload' ? 'primary.main' : 'divider', borderWidth: contractMode === 'upload' ? 2 : 1 }}>
          <CardContent>
            <FormControlLabel
              value="upload"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">Upload signed contract</Typography>
                  <Typography variant="caption" color="text.secondary">PDF, DOC or DOCX. Maximum 10 MB.</Typography>
                </Box>
              }
            />
            {contractMode === 'upload' && inner}
          </CardContent>
        </Card>
      );
    }

    return (
      <Card variant="outlined" sx={{ borderColor: entry.docId ? 'success.main' : 'divider' }}>
        <CardContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>PDF, DOC or DOCX. Maximum 10 MB.</Typography>
          {inner}
        </CardContent>
      </Card>
    );
  };

  // One upload row for a per-investor document type, under a given investor.
  // The investor name is the group sub-heading (rendered once), so this row is
  // labelled by the document type only. Keyed/stored by code + investor_id.
  const renderInvestorDocRow = (req, inv) => {
    const code = req.document_type_code;
    const entry = (docs[code] || {})[inv.investor_id] || {};
    return (
      <Card key={code} variant="outlined" sx={{ borderColor: entry.docId ? 'success.main' : 'divider' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="medium">{req.document_type_name}</Typography>
            {entry.docId && <Chip size="small" color="success" label="Uploaded" />}
          </Box>
          {entry.docId ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="body2" fontWeight="medium">{entry.fileName}</Typography>
              <Button size="small" onClick={() => setInvestorDoc(code, inv.investor_id, { file: null, docId: null, fileName: null })}>Replace</Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FileDropZone
                file={entry.file || null}
                onFile={(f) => acceptInvestorFile(code, inv.investor_id, f)}
                accept={ACCEPTED_MIME}
                disabled={entry.uploading}
                dense
                promptText="Choose a file, or drag and drop"
              />
              <Box>
                <Button variant="contained" size="small" onClick={() => handleInvestorUpload(req, inv)} disabled={!entry.file || entry.uploading} startIcon={entry.uploading ? <CircularProgress size={16} color="inherit" /> : null}>
                  {entry.uploading ? 'Uploading…' : 'Upload'}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // One contract-level document type. The signed contract keeps its reuse/upload
  // radio; other per-contract types are a plain upload card.
  const renderContractDoc = (req) => {
    const code = req.document_type_code;
    return (
      <Box key={code}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>{req.document_type_name}</Typography>
        {code === CONTRACT_SIGNED ? (
          <RadioGroup value={contractMode} onChange={(e) => setContractMode(e.target.value)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {previous && renderReuseCard()}
              {renderUploadCard(req, { asRadioOption: true })}
            </Box>
          </RadioGroup>
        ) : (
          renderUploadCard(req)
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={submitting || anyUploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Submit Contract for Approval</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {noDocumentsRequired
                ? 'No documents are required for this product. Confirm to submit this contract for approval.'
                : 'The following documents are required before this contract can be submitted for approval.'}
            </Typography>

            {/* Contract-level documents (one per contract) */}
            {contractDocs.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>Contract Documents</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {contractDocs.map(renderContractDoc)}
                </Box>
              </>
            )}

            {/* Per-investor documents — one sub-tab per investor (with a completion
                indicator), so many-investor contracts stay manageable. */}
            {investorDocs.length > 0 && investors.length > 0 && (
              <>
                {contractDocs.length > 0 && <Divider sx={{ my: 3 }} />}
                <Typography variant="h6" gutterBottom>Investor Documents</Typography>
                <Tabs
                  value={safeInvestorTab}
                  onChange={(e, v) => setActiveInvestorTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
                >
                  {investors.map((inv, i) => {
                    const complete = investorComplete(inv);
                    return (
                      <Tab
                        key={inv.investor_id}
                        {...a11yProps(i, 'submit-investor')}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <span>{inv.investor_name || `Investor ${inv.investor_id}`}</span>
                            {complete ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <Chip
                                label={`${investorUploadedCount(inv)}/${investorDocs.length}`}
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: '0.75rem' }}
                              />
                            )}
                          </Box>
                        }
                      />
                    );
                  })}
                </Tabs>
                {investors.map((inv, i) => (
                  <TabPanel key={inv.investor_id} value={safeInvestorTab} index={i} id="submit-investor" padding={0}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                      {investorDocs.map((req) => renderInvestorDocRow(req, inv))}
                    </Box>
                  </TabPanel>
                ))}
              </>
            )}

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting || anyUploading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? 'Submitting…' : 'Submit for Approval'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitForApprovalDialog;
